import { Button, Form, message, notification } from "antd";
import AButton from "core/presentation/components/Button";
import CustomForm from "core/presentation/components/CustomForm";
import CustomFormItem from "core/presentation/components/CustomFormItem";
import AModal from "core/presentation/components/Modal";
import PaginationTable from "core/presentation/components/PaginationTable";
import ASelect from "core/presentation/components/Select";
import ATable from "core/presentation/components/Tables";
import { RootState } from "core/presentation/store";
import { useAppDispatch, useAppSelector } from "core/presentation/store/hooks";
import ChangeTripStatusModal from "fleet/presentation/components/Trips/list/ChangeTripStatus";
import { fleetThunks } from "fleet/presentation/store/fleetSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userThunks } from "user/presentation/store/userSlice";

const OngoingTrips = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const { userProfile } = useAppSelector((state) => state.user);

  const [tripList, setTripList] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedTrip, setSelectedTrip] = useState<any | null>(null);
  const [tripStatus, setTripStatus] = useState();

  const { trips, isLoading, error, tripCount } = useSelector(
    (state: RootState) => state.fleet
  );

  const handleLocation = useCallback(
    async (lat: number, lng: number) => {
      if (lat === undefined || lng === undefined) {
        return "-";
      }
      try {
        const response = await userThunks.reverseGeoCode({
          latitude: lat,
          longitude: lng,
        });
        const { state, district, municipality } = response?.data?.data;
        return `${municipality}, ${district}, ${state}` || "-";
      } catch (error) {
        message.error("Failed to fetch location information");
        return `${lat}, ${lng}`;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (trips) {
        const updatedTrips = await Promise.all(
          trips?.map(async (trip: any) => {
            const from =
              trip?.locationFrom?.location ??
              (trip?.locationFrom?.lat && trip?.locationFrom?.lng
                ? await handleLocation(
                    trip?.locationFrom?.lat,
                    trip?.locationFrom?.lng
                  )
                : "-");
            const to =
              trip?.locationTo?.location ??
              (trip?.locationTo?.lat && trip?.locationTo?.lng
                ? await handleLocation(
                    trip?.locationTo?.lat,
                    trip?.locationTo?.lng
                  )
                : "-");
            return { ...trip, from, to };
          })
        );
        setTripList(updatedTrips);
      }
    };

    fetchData();
  }, [trips, handleLocation]);

  const getTrip = (page: number, input?: string, filterValue?: string) => {
    dispatch(
      fleetThunks.getAllTripThunk({
        page: page,
        ongoing: "&onGoingTrip=true",
      })
    );
  };
  useEffect(() => {
    getTrip(1, "", "");
  }, [dispatch]);

  const renderLocation = (text: string) => {
    const locationParts = text.split(",");
    const firstLine = locationParts.slice(0, 2).join(", ");
    const secondLine = locationParts.slice(2).join(", ");
    return (
      <div>
        <div>{firstLine}</div>
        <div style={{ color: "gray", fontSize: "0.9em" }}>{secondLine}</div>
      </div>
    );
  };

  const columns = [
    { title: "Trip No.", dataIndex: "tripNo", key: "tripNo" },
    { title: "Driver", dataIndex: "driver", key: "driver" },
    { title: "Vehicle No.", dataIndex: "vehicleNo", key: "vehicleNo" },
    {
      title: "Pickup Location",
      dataIndex: "from",
      key: "from",
      render: renderLocation,
    },
    {
      title: "Delivery Location",
      dataIndex: "to",
      key: "to",
      render: renderLocation,
    },
    { title: "Action", dataIndex: "action", key: "action" },
  ];

  const tripDataSource = useMemo(
    () =>
      tripList
        .map((data: any) => {
          let driverName = '-';

          if (data?.vehicle?.driver) {
            const driver = data.vehicle.driver;
            driverName = `${driver.firstName ?? ''} ${driver.middleName ?? ''} ${driver.lastName ?? ''}`.trim();
          } else if (data?.vehicle?.vehicleOwner) {
            const selfDriver = data.vehicle.vehicleOwner.find(
              (owner: any) => owner.isDriver === true
            );
            if (selfDriver) {
              driverName = `${selfDriver.ownerName} (Self Driver)`;
            }
          }

          return {
            tripNo: data.tripNumber,
            driver: driverName,
            vehicleNo: data?.vehicleNumber ?? "-",
            from: data.from,
            to: data.to,
            action: (
              <>
                <AButton
                  style={{
                    marginTop: "0",
                    padding: "5px",
                    color: "#F26533",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                  type="text"
                  onClick={() => setSelectedTrip(data)}
                >
                  Change Status
                </AButton>
                <AButton
                  style={{
                    marginTop: "0",
                    padding: "5px",
                    color: "#F26533",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                  type="text"
                  onClick={() => navigate(`/trip/details/${data?.tripId}?showMap=true`)}
                >
                  View
                </AButton>
              </>
            ),
          };
        }),
    [tripList, userProfile]
  );

  const handleTripStatusChange = (value: any) => {
    setTripStatus(value);
  };

  const handleChangeTripStatus = async (id: number, tripStatus: string) => {
    const response = await dispatch(
      fleetThunks.updateTripStatusThunk({ id, tripStatus })
    );
    if (response?.payload?.success) {
      setSelectedTrip(null);
      notification.success({
        message: "Success",
        description: "Trip status updated successfully!",
      });
    } else {
      notification.error({
        message: "Error",
        description:
          response?.payload?.error?.tripStatus ??
          "Invalid Trip Status, Please try again!",
      });
    }
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <PaginationTable
        dataSource={tripDataSource}
        columns={columns}
        tableTitle="Ongoing Trip List"
        loading={loading || isLoading || !tripList}
        totalData={tripCount}
        handleChange={getTrip}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      <AModal
        open={!!selectedTrip}
        footer={null}
        onCancel={() => {
          setSelectedTrip(null);
        }}
      >
        <CustomForm
          onFinish={() =>
            handleChangeTripStatus(selectedTrip?.tripId, "completed")
          }
        >
          <CustomFormItem label="Trip Status" name={"tripStatus"}>
            <ASelect
              value={tripStatus}
              onChange={handleTripStatusChange}
              options={[{ label: "Completed", value: "completed" }]}
              defaultValue={"completed"}
            />
          </CustomFormItem>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Form.Item>
        </CustomForm>
      </AModal>
    </div>
  );
};
export default OngoingTrips;
